import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global, globalMenu } from "@shared/libs/Global";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import { showPopupModal } from "@widgets/CommonModal";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAL_BAS_REG_006 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const sheetRef = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    sheet1: [],
  });

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const modifiedRows = gridRefs.current["grid1"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result = await onSave();
          if (!result) return;
        }
      }
      const params = {
        id: "sn_sal_bas_reg_006_01",
        params: searchRef.current.getFormdata(),
      };
      gridRefs.current["grid1"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        sheet1: data[1] || [],
      });
    } catch (error) {}
  };

  const onInsert = () => {
    const newRow = { cd_firm: userInfo.cd_firm };
    gridRefs.current["grid1"].current.addRow(newRow);
  };

  const onDelete = () => {
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const changes = gridRefs.current["grid1"].current.getChange();
      if (changes.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // insert + update 통합 중복 체크
      const allList = [...changes.insert, ...changes.update];
      if (!checkDuplicate(allList)) {
        await showDialog(
          "alter",
          "품목, 거래처코드, 거래처품목코드가 동일한 항목이 존재합니다."
        );
        return false;
      }

      await fnSave("SN_SAL_BAS_REG_006_SAVE_01", { mas_custitem: changes });

      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const onExcelUpload = async () => {
    try {
      const component = (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={["cd_cust", "cd_item", "cd_item_cust"]}
          tableName="mas_custitem"
          controllerName="SN_SAL_BAS_REG_006_SAVE_01"
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result?.excelUpload) {
        await onSearch();
      }

      if (result) {
        console.log("엑셀 업로드 완료:", result);
      } else {
        console.log("엑셀 업로드 취소됨");
      }
    } catch (error: any) {
      await showDialog("error", error.message);
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const rows: any[] = [];
      gridRefs.current["grid1"].current?.forEachFilteredNode((node: any) => {
        if (node?.data) rows.push(node.data);
      });

      sheetRef.current?.deleteAllRows?.();
      sheetRef.current?.applyTransaction({
        add: rows.map((r) => ({ ...r })),
      });

      const displayedCols = sheetRef.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());

      sheetRef.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (e: any) {
      await showDialog("error", e?.message || "Excel 다운로드 오류");
    }
  };

  /**
   * 회사, 품목코드, 거래처코드, 거래처품목코드가 동일한 행이 있으면 중복통제하는 공통 메서드
   */
  const checkDuplicate = (list: any[]) => {
    if (!list || list.length === 0) return true;

    const keySet = new Set();

    for (const row of list) {
      if (!row) continue;

      const cd_firm = row.cd_firm;
      const cd_item = row.cd_item;
      const cd_cust = row.cd_cust;
      const cd_item_cust = row.cd_item_cust;

      const key = `${cd_firm}_${cd_item}_${cd_cust}_${cd_item_cust}`;
      if (keySet.has(key)) {
        return false;
      }
      keySet.add(key);
    }
    return true;
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          // onRowDataReady: onRowDataReady,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        />
      </div>

      <div style={{ flex: "1 1 auto", display: "flex", gap: "10px" }}>
        <div style={{ flex: "1 1 100%", overflow: "auto" }}>
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세내역",
            }}
            events={grids.grid1.events}
          />
        </div>
      </div>

      <div style={{ display: "none" }}>
        <SnGrid
          ref={sheetRef}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource}
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_SAL_BAS_REG_006;
